import React from 'react';
import { Col, Placeholder, Row, Table } from 'react-bootstrap';
import CardLayout from '../../../../../shared/components/card/CardLayout';
import { DashUsersSkeleton } from '../../dashboard/components/CampaignDashboardSkeletons';
import { DashboardRightSideSkeleton } from './CampaignOverviewDetailSkeleton';

const CardHeaderSkeleton = ({ buttonClass }: { buttonClass?: string }) => (
    <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center flex-grow-1">
            <Placeholder className="skeleton skeleton-header-icon mr-2" />
            <Placeholder xs={3} className="skeleton" />
        </div>
        {buttonClass && <Placeholder className={`skeleton skeleton-action-btn ${buttonClass}`} />}
    </div>
);

// Redovi ugovora (i loader za infinite scroll)
export function DashboardContractRowsSkeleton({
    rows = 6,
    hideCosts = false,
    withHeader = true,
}: {
    rows?: number;
    hideCosts?: boolean;
    withHeader?: boolean;
}) {
    // Postings used, Postings booked, Followers, Avg. Reach, Avg. ER (+ Avg. cpm, Cost)
    const valueColumns = hideCosts ? 5 : 7;
    const columns = Array.from({ length: valueColumns });
    const alignClass = (i: number) => (i === valueColumns - 1 ? 'text-end text-right' : 'text-center');

    return (
        <Placeholder as="div" animation="glow" aria-hidden="true">
            <Table className="skeleton-table table-vertical-center mb-0">
                {withHeader && (
                    <thead className="border-bottom">
                        <tr>
                            <th>
                                <Placeholder className="skeleton skeleton-table-head" />
                            </th>
                            {columns.map((_, i) => (
                                <th key={i} className={alignClass(i)}>
                                    <Placeholder className="skeleton skeleton-table-head" />
                                </th>
                            ))}
                        </tr>
                    </thead>
                )}
                <tbody>
                    {Array.from({ length: rows }).map((_, rowIndex) => (
                        <tr key={rowIndex}>
                            <td>
                                <div className="d-flex align-items-center">
                                    <Placeholder className="skeleton skeleton-contract-avatar" />
                                    <Placeholder className="skeleton skeleton-table-name ml-2" />
                                </div>
                            </td>
                            {columns.map((_, i) => (
                                <td key={i} className={alignClass(i)}>
                                    <Placeholder className="skeleton skeleton-table-cell" />
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </Table>
        </Placeholder>
    );
}

export function DashboardContractCardSkeleton({ hideCosts = false }: { hideCosts?: boolean }) {
    const stats = hideCosts ? 3 : 4;

    return (
        <CardLayout classCard="mb-3">
            <Placeholder as="div" animation="glow" aria-hidden="true">
                <CardHeaderSkeleton buttonClass="skeleton-btn-sm" />
                <Row className="align-items-center my-3 justify-content-between">
                    <Col md={6} className="d-flex justify-content-between justify-content-lg-start" style={{ gap: 24 }}>
                        {Array.from({ length: stats }).map((_, i) => (
                            <div key={i}>
                                <Placeholder className="skeleton skeleton-stat-value d-block" />
                                <Placeholder className="skeleton skeleton-stat-label d-block mt-2" />
                            </div>
                        ))}
                    </Col>
                    {!hideCosts && (
                        <Col md={4}>
                            <div className="d-flex justify-content-between mb-2">
                                <Placeholder xs={4} className="skeleton" />
                                <Placeholder xs={4} className="skeleton" />
                                <Placeholder xs={1} className="skeleton" />
                            </div>
                            <Placeholder className="skeleton skeleton-progress d-block" />
                        </Col>
                    )}
                </Row>
            </Placeholder>
            <DashboardContractRowsSkeleton hideCosts={hideCosts} />
        </CardLayout>
    );
}

// Redovi linked kampanja (i loader za infinite scroll)
export function DashboardLinkedRowsSkeleton({ rows = 3 }: { rows?: number }) {
    return (
        <Placeholder as="div" animation="glow" aria-hidden="true">
            {Array.from({ length: rows }).map((_, i) => (
                <div key={i} className="d-flex align-items-center py-2 border-bottom">
                    <Placeholder className="skeleton skeleton-linked-image" />
                    <div className="ml-2 flex-grow-1">
                        <Placeholder xs={5} className="skeleton d-block" />
                        <Placeholder xs={3} className="skeleton d-block mt-1" />
                    </div>
                    <Placeholder className="skeleton skeleton-badge" />
                </div>
            ))}
        </Placeholder>
    );
}

export function DashboardLinkedCampaignCardSkeleton({ withButton = true }: { withButton?: boolean }) {
    return (
        <CardLayout classCard="mb-3">
            <Placeholder as="div" animation="glow" aria-hidden="true">
                <CardHeaderSkeleton buttonClass={withButton ? 'skeleton-btn-sm skeleton-btn-wide' : undefined} />
            </Placeholder>
            <div className="my-3">
                <DashboardLinkedRowsSkeleton />
            </div>
        </CardLayout>
    );
}

export function LongTermDashboardSkeleton() {
    return (
        <Row className="flex-column-reverse flex-md-row">
            <Col md={12} lg={8}>
                <DashboardContractCardSkeleton />
                <DashboardLinkedCampaignCardSkeleton />
            </Col>
            <Col md={12} lg={4}>
                <DashboardRightSideSkeleton />
            </Col>
        </Row>
    );
}
